import React from 'react';

interface Testimonial {
  id: string | number;
  name: string;
  age?: string;
  goal?: string;
  content: string;
  result?: string;
  rating: number;
  trainer_name: string;
  created?: string;
}

interface TestimonialsSectionProps {
  trainers: any[];
  reviews: any[];
  title?: string;
  subtitle?: string;
  defaultContent?: string;
  defaultResult?: string;
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  trainers,
  reviews,
  title = "Poznaj opinie o naszych trenerach",
  subtitle,
  defaultContent = "Świetna współpraca z trenerem! Profesjonalne podejście i widoczne efekty.",
  defaultResult = "Polecam!"
}) => {
  // If we have real reviews, use them
  if (reviews && reviews.length > 0) {
    // Sort reviews by date (most recent first) and take top 3
    const sortedReviews = reviews
      .sort((a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime())
      .slice(0, 3);

    // Process real reviews from API
    const displayTestimonials: Testimonial[] = sortedReviews.map((review: any) => {
      // Clean up the name - if empty or just whitespace, use a default format
      let clientName = review.creator_full_name || 'Anonimowy klient';
      if (!clientName || clientName.trim() === ' ' || clientName.trim() === '') {
        clientName = 'Zadowolony klient';
      }

      // Find correct trainer name from trainers list instead of using outdated trainer_full_name
      const correctTrainer = trainers.find((t: any) => t.user_id === review.trainer);
      const trainerName = correctTrainer?.full_name || review.trainer_full_name || 'Trener';

      // Ensure we have valid content
      let content = review.text || defaultContent;
      if (!content || content.trim() === '') {
        content = defaultContent;
      }

      return {
        id: review.id,
        name: clientName.trim(),
        content: content.trim(),
        rating: review.rating || 5,
        trainer_name: trainerName,
        created: review.created ? new Date(review.created).toLocaleDateString('pl-PL') : new Date().toLocaleDateString('pl-PL'),
        result: review.rating >= 4 ? defaultResult : "Warto spróbować"
      };
    });

    return (
      <section className="landing-testimonials">
        <div className="landing-container">
          <div className="landing-section-header">
            <h2>{title}</h2>
            <p>{subtitle || `Rzeczywiste oceny od klientów z ${trainers[0]?.location || 'Poznania'} i okolic`}</p>
          </div>

          <div className="landing-testimonials-grid">
            {displayTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="landing-testimonial-card">
                <div className="landing-testimonial-content">
                  <p className="landing-testimonial-text">"{testimonial.content}"</p>
                  <div className="landing-testimonial-result">
                    <span className="landing-testimonial-result-label">Wynik:</span>
                    <span className="landing-testimonial-result-value">{testimonial.result}</span>
                  </div>
                </div>
                <div className="landing-testimonial-author">
                  <div className="landing-testimonial-info">
                    <h4 className="landing-testimonial-name">{testimonial.name}</h4>
                    <p className="landing-testimonial-details">Trener: {testimonial.trainer_name} • {testimonial.created}</p>
                  </div>
                  <div className="landing-testimonial-rating">
                    {[...Array(Math.round(testimonial.rating))].map((_, i) => (
                      <span key={i} className="landing-testimonial-star">⭐</span>
                    ))}
                    <span className="landing-testimonial-rating-text">
                      {testimonial.rating.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // If no real reviews, create placeholder testimonials based on top trainers
  const placeholderTestimonials = trainers.slice(0, 3).map((trainer: any, index: number) => ({
    id: `placeholder-${trainer.id}`,
    name: `${['Anna', 'Piotr', 'Magda'][index % 3]} ${['Nowak', 'Kowalski', 'Wiśniewski'][index % 3][0]}.`,
    content: defaultContent.replace('trenerem', `${trainer.full_name}`),
    rating: trainer.rating?.average_rating || 5,
    trainer_name: trainer.full_name,
    created: new Date().toLocaleDateString('pl-PL'),
    result: defaultResult
  }));

  return (
    <section className="landing-testimonials">
      <div className="landing-container">
        <div className="landing-section-header">
          <h2>{title}</h2>
          <p>{subtitle || `Rzeczywiste oceny od klientów z ${trainers[0]?.location || 'Poznania'} i okolic`}</p>
        </div>

        <div className="landing-testimonials-grid">
          {placeholderTestimonials.map((testimonial) => (
            <div key={testimonial.id} className="landing-testimonial-card">
              <div className="landing-testimonial-content">
                <p className="landing-testimonial-text">"{testimonial.content}"</p>
                <div className="landing-testimonial-result">
                  <span className="landing-testimonial-result-label">Wynik:</span>
                  <span className="landing-testimonial-result-value">{testimonial.result}</span>
                </div>
              </div>
              <div className="landing-testimonial-author">
                <div className="landing-testimonial-info">
                  <h4 className="landing-testimonial-name">{testimonial.name}</h4>
                  <p className="landing-testimonial-details">Trener: {testimonial.trainer_name} • {testimonial.created}</p>
                </div>
                <div className="landing-testimonial-rating">
                  {[...Array(Math.round(testimonial.rating))].map((_, i) => (
                    <span key={i} className="landing-testimonial-star">⭐</span>
                  ))}
                  <span className="landing-testimonial-rating-text">
                    {testimonial.rating.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;